import cn from 'classnames';
import React, { Component, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import keycode from 'keycode';
import { Input, Icon, Backdrop, Header } from '@economic/taco';

import { useApplicationContext, useRouteAuthorisation } from '@economic/react-app';
import GlobalKeyboardShortcuts from '../../../shared/components/GlobalKeyboardShortcuts';
import useCachedQuery from '../../../shared/hooks/useCachedQuery';
import Results from './Results';
import generatePageShortcuts from './util/generatePageShortcuts';
import generateRouteShortcuts from './util/generateRouteShortcuts';
import { Book } from '../../../../types';
import { Journal } from '../../../../types/internal-api';
import './style.css';
import { t } from '../../../shared/components/l10n/l10n';
import workflowsTracker from '../../../views/Workflows/utils/workflowsTracker';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    data?: any;
    navigate: any;
    routes?: any;
    texts?: any;
}

interface State {
    cache: any[];
    currentView: any[];
    isOpen: boolean;
    results: any[];
    value: string;
}

const isFormElement = (element: HTMLElement) => {
    const formElementNodeNames = ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'FIELDSET'];
    return formElementNodeNames.includes(element.nodeName);
};

class Quicksearch extends Component<Props, State> {
    state = {
        cache: generateRouteShortcuts(this.props.data, this.props.routes),
        currentView: [],
        isOpen: false,
        results: [],
        value: '',
    };

    // refs
    input: React.RefObject<any> = React.createRef();

    componentDidUpdate(prevProps, prevState) {
        if (this.props.routes !== prevProps.routes || JSON.stringify(this.props.data) !== JSON.stringify(prevProps.data)) {
            this.setState({ cache: generateRouteShortcuts(this.props.data, this.props.routes) });
        }

        if (!prevState.isOpen && this.state.isOpen) {
            this.input.current?.focus();
        }
    }

    render() {
        const { cache, isOpen, results, value } = this.state;

        const classNames = cn('relative', this.props.className, {
            'quicksearch--open absolute right-0 top-0 w-[300px]': isOpen,
        });
        const icon = isOpen ? 'close' : 'search-bold';

        const searchInput = (
            <label className="relative">
                <Input
                    className="!border-transparent outline-none shadow-none w-full !bg-white/[0.08] hover:bg-white/[0.16] focus:bg-white/[0.16] rounded h-10 pr-10 pl-2 focus:!yt-focus-dark"
                    onChange={this.onChange}
                    onFocus={this.open}
                    onKeyDown={this.onKeyDown}
                    placeholder={t`QUICK_SEARCH_PLACEHOLDER`}
                    ref={this.input}
                    type="search"
                    value={value}
                    autoFocus
                />
                <Icon
                    className="absolute mb-0 right-0 cursor-pointer hover:text-white focus:outline-none hover:outline-none !h-10 mr-2"
                    onClick={this.onClick}
                    tabIndex={-1}
                    name={icon}
                />
            </label>
        );

        return (
            <GlobalKeyboardShortcuts handlers={{ '.': this.open }}>
                <div id="quicksearch" className={classNames}>
                    {isOpen && <Backdrop className="z-[1] !bg-blue-900/50" onClick={this.close} />}
                    <>
                        <Header.Button
                            icon="search-bold"
                            aria-label="quick search"
                            onClick={this.open}
                            className={isOpen ? 'hidden' : undefined}
                        />
                        {isOpen && searchInput}
                    </>
                    {isOpen && (
                        <div className="quicksearch__results bg-white overflow-y-auto absolute w-full mt-1 text-sm rounded p-1">
                            {results.length > 0 ? (
                                <Results
                                    cache={cache}
                                    close={this.close}
                                    results={results}
                                    navigate={this.props.navigate}
                                    searchValue={value}
                                />
                            ) : (
                                <p className="bg-white m-0">{t`QUICK_SEARCH_NO_RESULTS`}</p>
                            )}
                        </div>
                    )}
                </div>
            </GlobalKeyboardShortcuts>
        );
    }

    onClick = (e: MouseEvent<SVGSVGElement>) => {
        e.preventDefault();

        if (this.state.isOpen) {
            this.close();
        } else {
            this.open();
        }
    };

    onChange = e => {
        const { value } = e.target;

        this.setState(state => ({
            isOpen: true,
            results: this.search(value, state.cache, state.currentView),
            value,
        }));
    };

    onKeyDown = e => {
        if (e.keyCode === keycode('tab')) {
            this.close();
            return;
        }

        if (e.keyCode === keycode('escape')) {
            e.preventDefault();
            this.close();
            this.input.current.blur();
        }
    };

    open = (event?: any) => {
        if (event?.target && isFormElement(event.target as HTMLElement)) {
            return;
        }

        if (event?.preventDefault) {
            event.preventDefault();
        }

        const setPageShortcuts = shortcuts =>
            this.setState(state => ({
                currentView: shortcuts,
                isOpen: true,
                results: this.search(state.value, state.cache, shortcuts),
            }));

        this.setState(
            {
                isOpen: true,
            },
            () => {
                generatePageShortcuts().then(shortcuts => {
                    if (shortcuts.length) {
                        setPageShortcuts(shortcuts);
                    } else {
                        // check for shortcuts again after 2.5s, incase they opened quicksearch before the page loaded
                        setTimeout(() => {
                            generatePageShortcuts().then(pageShortcuts => {
                                if (pageShortcuts.length) {
                                    setPageShortcuts(pageShortcuts);
                                }
                            });
                        }, 2500);
                    }
                });
            }
        );
    };

    close = () =>
        this.setState({
            isOpen: false,
            value: '',
        });

    search = (value, cache, currentView) => {
        const data = [...currentView, ...cache];

        return value.length > 0
            ? data.filter(c => c.title?.toLowerCase().includes(value.toLowerCase()) && c.type !== 'tab')
            : currentView;
    };
}

export default props => {
    const { configuration } = useApplicationContext();

    const [hasJournalAccess] = useRouteAuthorisation('/accounting/journals/:id');
    const [hasInventoryBookAccess] = useRouteAuthorisation('/inventory/books/:id');
    const [hasProjectBookAccess] = useRouteAuthorisation('/projects/usage/:id');
    const navigate = useNavigate();

    const journalsQuery = useCachedQuery<Journal[]>('/journals?type=classic', [], { skip: !hasJournalAccess });
    const booksQuery = useCachedQuery<Book[]>('/inventory/books', [], { skip: !hasInventoryBookAccess || !hasProjectBookAccess });

    const onNavigate = (path: string) => {
        navigate(path);

        switch (path) {
            case '/settings/workflows':
            case '/settings/workflows/list':
                workflowsTracker('navigated-to-workflows', {
                    source: 'quicksearch',
                });
                break;
            default:
                break;
        }
    };

    return (
        <Quicksearch
            {...props}
            data={{
                journals: hasJournalAccess ? journalsQuery.data : null,
                inventoryBooks: hasInventoryBookAccess && hasProjectBookAccess ? booksQuery.data : null,
            }}
            routes={configuration.routes}
            navigate={onNavigate}
        />
    );
};
